/**
 * Service for handling LinkedIn connections upload and processing
 * All CSV parsing is handled by the backend to avoid duplication
 */

import {
  DetailedConnection,
  UploadConnectionsResponse,
  AsyncUploadStartResponse,
  UploadStatusResponse,
  PopulateScoresStartResponse,
  PopulateScoresStatusResponse,
  ConnectionsError,
  ConnectionsScore,
  SearchGroundedStartResponse,
  SearchGroundedStatusResponse,
} from '../types/connections';

// In dev the CRA proxy (package.json "proxy") forwards /api/* to the backend
// so we use relative URLs — no CORS, port configured in exactly one place.
// In production REACT_APP_API_URL is set to the actual backend origin.
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// new URL() requires an absolute base when the path is relative.
const _origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
const buildURL = (path: string) =>
  API_BASE_URL ? new URL(`${API_BASE_URL}${path}`) : new URL(path, _origin);

const PENDING_ENABLE_MESSAGE =
  'Your account is pending admin enablement. Please contact an admin for access.';

const extractConnectionsErrorMessage = (response: Response, payload: any, fallback: string): string => {
  const code = String(payload?.code || payload?.detail?.code || '').toUpperCase();
  if (response.status === 403 && (code === 'USER_PENDING_ENABLE' || code === 'USER_DISABLED_BY_ADMIN' || code === 'USER_PENDING_APPROVAL')) {
    return PENDING_ENABLE_MESSAGE;
  }
  if (typeof payload?.message === 'string' && payload.message) return payload.message;
  if (typeof payload?.detail === 'string' && payload.detail) return payload.detail;
  if (typeof payload?.detail?.message === 'string' && payload.detail.message) return payload.detail.message;
  return fallback;
};

const UPLOAD_JOB_STORAGE_KEY = 'connections_upload_job';
const SCORE_JOB_STORAGE_KEY = 'connections_score_job';
const GROUNDED_JOB_STORAGE_KEY = 'connections_grounded_job';

/** Get stored upload job id from localStorage (persists across reloads) */
export const getStoredUploadJobId = (): string | null => {
  try {
    return localStorage.getItem(UPLOAD_JOB_STORAGE_KEY);
  } catch {
    return null;
  }
};

/** Store upload job id in localStorage */
export const setStoredUploadJobId = (jobId: string | null): void => {
  try {
    if (jobId) localStorage.setItem(UPLOAD_JOB_STORAGE_KEY, jobId);
    else localStorage.removeItem(UPLOAD_JOB_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** Get stored score job id from localStorage (persists across reloads) */
export const getStoredScoreJobId = (): string | null => {
  try {
    return localStorage.getItem(SCORE_JOB_STORAGE_KEY);
  } catch {
    return null;
  }
};

/** Store score job id in localStorage */
export const setStoredScoreJobId = (jobId: string | null): void => {
  try {
    if (jobId) localStorage.setItem(SCORE_JOB_STORAGE_KEY, jobId);
    else localStorage.removeItem(SCORE_JOB_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Upload CSV file to backend for processing.
 * When async=true (default): returns immediately with jobId; process runs in background. Use getUploadStatus to poll.
 * When async=false: waits for completion (legacy; may timeout for large files).
 */
export const uploadConnectionsCSV = async (
  file: File,
  authToken: string,
  limit?: number,
  asyncMode: boolean = true,
  notifyEmail?: string,
  /** When true, only enrich profiles not yet saved — advances through the CSV. Default false: new rows first, then updates. */
  newOnly: boolean = false,
): Promise<UploadConnectionsResponse | AsyncUploadStartResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const url = buildURL(`/api/connections/upload`);
  // Always send limit (even 0 = "no limit") so the backend env-var default never
  // silently overrides an explicit admin choice. undefined = caller didn't provide one.
  if (limit !== undefined) url.searchParams.append('limit', limit.toString());
  if (asyncMode) url.searchParams.append('async', 'true');
  if (notifyEmail && notifyEmail.trim()) url.searchParams.append('notifyEmail', notifyEmail.trim());
  url.searchParams.append('newOnly', newOnly ? 'true' : 'false');

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${authToken}` },
    body: formData,
  });

  if (response.status === 202) {
    const data: AsyncUploadStartResponse = await response.json();
    return data;
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData: ConnectionsError = await response.json();
      errorMessage = extractConnectionsErrorMessage(response, errorData, errorMessage);
      if (errorData.details) {
        errorMessage += ` - ${JSON.stringify(errorData.details)}`;
      }
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }

  return (await response.json()) as UploadConnectionsResponse;
};

/**
 * Poll upload job status. Returns 404 if job not found (e.g. server restarted).
 */
export const getUploadStatus = async (
  authToken: string,
  jobId: string
): Promise<UploadStatusResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/connections/upload/status/${encodeURIComponent(jobId)}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${authToken}` },
    }
  );
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const err: ConnectionsError = await response.json();
      errorMessage = extractConnectionsErrorMessage(response, err, errorMessage);
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }
  return await response.json();
};

/**
 * Get user's detailed connections from Firebase — no limit, always returns all.
 */
export const getDetailedConnections = async (
  authToken: string
): Promise<{ connections: DetailedConnection[]; total: number }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/connections/detailed`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData: ConnectionsError = await response.json();
        errorMessage = extractConnectionsErrorMessage(response, errorData, errorMessage);
      } catch (parseError) {
        try {
          const text = await response.text();
          if (text) errorMessage = text;
        } catch (textError) {
          // Keep original error message
        }
      }
      throw new Error(errorMessage);
    }
    
    return await response.json();
  } catch (error: any) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Fetch failed: ${error?.message || String(error)}`);
  }
};

/**
 * Start async populate-scores job. Returns jobId immediately; run continues in background.
 * Poll getPopulateScoresStatus for progress. Survives page reload when jobId is stored.
 */
export const startPopulateScoresJob = async (
  authToken: string
): Promise<PopulateScoresStartResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/connections/populate-scores?async=true`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (response.status !== 202) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData: ConnectionsError = await response.json();
      errorMessage = errorData.message || errorData.detail || errorMessage;
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return { jobId: data.jobId, status: 'processing', message: data.message };
};

/**
 * Poll populate-scores job status. Returns 404 if job not found (e.g. server restarted).
 */
export const getPopulateScoresStatus = async (
  authToken: string,
  jobId: string
): Promise<PopulateScoresStatusResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/connections/populate-scores/status/${encodeURIComponent(jobId)}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${authToken}` },
    }
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Job not found');
    }
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData: ConnectionsError = await response.json();
      errorMessage = errorData.message || errorData.detail || errorMessage;
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * @deprecated Use startPopulateScoresJob + getPopulateScoresStatus for async (default).
 * Sync mode: waits for completion. May timeout for large datasets.
 */
export const populateConnectionsScores = async (
  authToken: string
): Promise<{ message: string; total_connections: number; companies_processed: number; documents_created: number }> => {
  const response = await fetch(`${API_BASE_URL}/api/connections/populate-scores?async=false`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData: ConnectionsError = await response.json();
      errorMessage = errorData.message || errorData.detail || errorMessage;
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Delete a single connection document (only if it belongs to the current user).
 */
export const deleteConnection = async (
  authToken: string,
  connectionId: string
): Promise<{ message: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/connections/detailed/${encodeURIComponent(connectionId)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData: ConnectionsError = await response.json();
      errorMessage = errorData.message || errorData.detail || errorMessage;
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }
  return await response.json();
};

/**
 * Update a connection's professional and/or cohort alumni tiers (user feedback).
 * professionalTier: 1, 2, 3, or null; cohortAlumniTier: 1, 2, or null.
 */
export const updateConnectionTiers = async (
  authToken: string,
  connectionId: string,
  tiers: { professionalTier?: number | null; cohortAlumniTier?: number | null }
): Promise<{ message: string }> => {
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE_URL}/api/connections/detailed/${encodeURIComponent(connectionId)}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tiers),
      }
    );
  } catch (networkErr: any) {
    throw new Error(
      networkErr?.message?.toLowerCase().includes('failed') || networkErr?.message === 'Load failed'
        ? 'Could not reach the server. Check that the backend is running and CORS allows PATCH.'
        : `Update failed: ${networkErr?.message || String(networkErr)}`
    );
  }
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData = await response.json() as { message?: string; detail?: string | Array<{ msg?: string }> };
      const d = errorData.detail;
      if (typeof d === 'string') errorMessage = d;
      else if (Array.isArray(d) && d.length > 0) errorMessage = d.map((e) => e.msg || JSON.stringify(e)).join('; ');
      else if (errorData.message) errorMessage = errorData.message;
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }
  return await response.json();
};

/**
 * Delete all detailed connections (Firestore connections collection) for the current user.
 */
export const deleteDetailedConnections = async (authToken: string): Promise<{ message: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/connections/detailed`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData: ConnectionsError = await response.json();
      errorMessage = errorData.message || errorData.detail || errorMessage;
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }
  return await response.json();
};

/**
 * Update a company score's manual override and/or reason.
 */
export const updateCompanyScore = async (
  authToken: string,
  scoreId: string,
  update: { manualScoreOverride?: number | null; manualScoreReason?: string | null }
): Promise<{ message: string }> => {
  const body: Record<string, unknown> = {};
  if (update.manualScoreOverride !== undefined) body.manualScoreOverride = update.manualScoreOverride;
  if (update.manualScoreReason !== undefined) body.manualScoreReason = update.manualScoreReason;

  const response = await fetch(`${API_BASE_URL}/api/connections/scores/${encodeURIComponent(scoreId)}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData: ConnectionsError = await response.json();
      errorMessage = errorData.message || errorData.detail || errorMessage;
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }
  return await response.json();
};

/**
 * Delete a single connections-score document (only if it belongs to the current user).
 */
export const deleteConnectionsScoreById = async (
  authToken: string,
  scoreId: string
): Promise<{ message: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/connections/scores/${encodeURIComponent(scoreId)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData: ConnectionsError = await response.json();
      errorMessage = errorData.message || errorData.detail || errorMessage;
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }
  return await response.json();
};

/**
 * Delete all connections-score documents (company scores) for the current user.
 */
export const deleteConnectionsScores = async (authToken: string): Promise<{ message: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/connections/scores`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
  });
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData: ConnectionsError = await response.json();
      errorMessage = errorData.message || errorData.detail || errorMessage;
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }
  return await response.json();
};

/** Get stored search-grounded job id from localStorage */
export const getStoredGroundedJobId = (): string | null => {
  try {
    return localStorage.getItem(GROUNDED_JOB_STORAGE_KEY);
  } catch {
    return null;
  }
};

/** Store search-grounded job id in localStorage */
export const setStoredGroundedJobId = (jobId: string | null): void => {
  try {
    if (jobId) localStorage.setItem(GROUNDED_JOB_STORAGE_KEY, jobId);
    else localStorage.removeItem(GROUNDED_JOB_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Start search-grounded enrichment job for top connections (Tier 1 first, then 2/3/null).
 * Skips only already search-grounded rows. Capped at 200 per user.
 */
export const startSearchGroundedEnrich = async (
  authToken: string,
  batchSize?: number
): Promise<SearchGroundedStartResponse> => {
  const url = buildURL(`/api/connections/search-grounded-enrich`);
  if (batchSize && batchSize > 0) url.searchParams.append('batch_size', batchSize.toString());

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: { Authorization: `Bearer ${authToken}` },
  });

  if (response.status !== 202) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const err: ConnectionsError = await response.json();
      errorMessage = err.detail || err.message || errorMessage;
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }

  return await response.json();
};

/**
 * Poll search-grounded enrichment job status.
 */
export const getSearchGroundedStatus = async (
  authToken: string,
  jobId: string
): Promise<SearchGroundedStatusResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/connections/search-grounded-enrich/status/${encodeURIComponent(jobId)}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${authToken}` },
    }
  );
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const err: ConnectionsError = await response.json();
      errorMessage = err.detail || err.message || errorMessage;
    } catch {
      try {
        const text = await response.text();
        if (text) errorMessage = text;
      } catch {
        // keep errorMessage
      }
    }
    throw new Error(errorMessage);
  }
  return await response.json();
};

/**
 * Get connections-score documents (companies with scores)
 */
export const getConnectionsScores = async (
  authToken: string
): Promise<{ scores: ConnectionsScore[]; total: number }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/connections/scores`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData: ConnectionsError = await response.json();
        errorMessage = errorData.message || errorData.detail || errorMessage;
      } catch (parseError) {
        try {
          const text = await response.text();
          if (text) errorMessage = text;
        } catch (textError) {
          // Keep original error message
        }
      }
      throw new Error(errorMessage);
    }
    
    return await response.json();
  } catch (error: any) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Fetch scores failed: ${error?.message || String(error)}`);
  }
};

/**
 * Trigger a standalone Adzuna fetch: populates job-post-urls for the user's
 * top connected companies (excluding Greenhouse-covered and Google).
 * Adzuna jobs are written to the shared job database with full descriptions
 * so they benefit all users during process-jobs.
 */
export interface AdzunaFetchResult {
  message: string;
  apiCalls: number;
  jobsWritten: number;
  companiesFetched: string[];
  companiesCached: string[];
  /** True when response is from `/adzuna-fetch-finance` (Accounting & Finance category). */
  finance?: boolean;
  /** True when write-ordering rotates across distinct titles first. */
  titleDiversityEnabled?: boolean;
  /** Distinct normalized titles present in Adzuna API payload. */
  uniqueTitlesInApi?: number;
  /** Distinct normalized titles actually written this run. */
  uniqueTitlesWritten?: number;
  /** Name of the diversity strategy applied server-side. */
  titleDiversityMode?: string;
}

export const startAdzunaFetch = async (
  token: string,
  limit: number = 20,
): Promise<AdzunaFetchResult> => {
  const url = buildURL(`/api/connections/adzuna-fetch`);
  url.searchParams.append('limit', String(limit));
  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    let msg = 'Adzuna fetch failed';
    try {
      const err = await response.json();
      msg = err.detail || err.message || msg;
    } catch {
      // keep default
    }
    throw new Error(msg);
  }
  return response.json();
};

/**
 * Adzuna finance: Accounting & Finance category, scoped to server targets (banks / AM first; see
 * backend app/data/adzuna_finance_targets.json). Writes job-post-urls with `adzunaSearchMode=finance`.
 * Skips if refreshed today unless force=true.
 *
 * @param maxEmployers Optional cap on how many target employers to query (first N in order); omit for all.
 */
export const startAdzunaFinanceFetch = async (
  token: string,
  limit: number = 30,
  force: boolean = false,
  maxEmployers?: number,
  usOnly: boolean = false,
  seniorTitlesOnly: boolean = false,
): Promise<AdzunaFetchResult> => {
  const url = buildURL(`/api/connections/adzuna-fetch-finance`);
  url.searchParams.append('limit', String(limit));
  url.searchParams.append('force', force ? 'true' : 'false');
  if (maxEmployers !== undefined && maxEmployers >= 1) {
    url.searchParams.append('max_employers', String(maxEmployers));
  }
  if (usOnly) {
    url.searchParams.append('us_only', 'true');
  }
  if (seniorTitlesOnly) {
    url.searchParams.append('senior_titles_only', 'true');
  }
  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    let msg = 'Finance Adzuna fetch failed';
    try {
      const err = await response.json();
      msg = err.detail || err.message || msg;
    } catch {
      // keep default
    }
    throw new Error(msg);
  }
  return response.json();
};

/** Background job board scraper — live per-platform progress (updated while running) */
export interface ScraperPlatformProgress {
  total: number;
  processed: number;
  inserted: number;
}

export interface JobScrapeStartResponse {
  jobId: string;
  message: string;
  status: string;
  startedAt: string;
}

export interface JobScrapeStatusResponse {
  jobId: string;
  status: string;
  message?: string;
  startedAt?: string;
  completedAt?: string;
  /** Live per-platform progress; available from the first poll while status is "processing" */
  progress?: Record<string, ScraperPlatformProgress>;
  result?: {
    durationSec: number;
    inserted: number;
    updated: number;
    jobsWritten: number;
    discoveredTotal: number;
    skippedExisting?: number;
    skippedCutoff?: number;
    skippedRegion?: number;
    skippedDate?: number;
    skippedCap?: number;
    maxTotalJobsCap?: number | null;
    maxPostingsPerCompany?: number;
    globalTotalWritten?: number;
    errors?: Array<{ company: string; platform: string; url: string; error: string }>;
  };
  error?: string;
  emailNotification?: string;
  emailNotificationDetail?: string;
}

export const JOB_SCRAPE_JOB_STORAGE_KEY = 'tsja_job_scrape_job';

export const getStoredJobScrapeJobId = (): string | null => {
  try {
    return localStorage.getItem(JOB_SCRAPE_JOB_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setStoredJobScrapeJobId = (jobId: string | null): void => {
  try {
    if (jobId) localStorage.setItem(JOB_SCRAPE_JOB_STORAGE_KEY, jobId);
    else localStorage.removeItem(JOB_SCRAPE_JOB_STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const startJobScrapeRun = async (
  token: string,
  notifyEmail?: string | null,
): Promise<JobScrapeStartResponse> => {
  const url = buildURL(`/api/connections/job-scrape`);
  if (notifyEmail?.trim()) {
    url.searchParams.append('notifyEmail', notifyEmail.trim());
  }
  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    let msg = 'Job scrape failed to start';
    try {
      const err = await response.json();
      msg = err.detail || err.message || msg;
    } catch {
      // keep default
    }
    throw new Error(msg);
  }
  return response.json();
};

/**
 * Build the SSE stream URL for a scraper job.
 * Token is passed as a query param because EventSource cannot set headers.
 */
export const getJobScrapeStatusStreamUrl = (jobId: string, token: string): string =>
  `${API_BASE_URL}/api/connections/job-scrape/status-stream/${jobId}?token=${encodeURIComponent(token)}`;

export const getJobScrapeStatus = async (
  token: string,
  jobId: string,
): Promise<JobScrapeStatusResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/connections/job-scrape/status/${jobId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    let msg = 'Could not get job scrape status';
    try {
      const err = await response.json();
      msg = err.detail || err.message || msg;
    } catch {
      // keep default
    }
    throw new Error(msg);
  }
  return response.json();
};

export const getCurrentScrapeJob = async (token: string): Promise<{ jobId: string; status: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/connections/job-scrape/current`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('No active scraper job');
  return response.json();
};

export interface LastScrapeRunResponse {
  startedAt?: string;
  completedAt?: string;
  status?: 'completed' | 'failed' | 'running';
  durationSec?: number;
  inserted: number;
  progress: Record<string, ScraperPlatformProgress>;
  errors?: Array<{ company: string; platform: string; url: string; error: string }>;
  error?: string;
}

export const getLastScrapeRun = async (token: string): Promise<LastScrapeRunResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/connections/job-scrape/last-run`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('No last scrape run found');
  return response.json();
};

/** Vertex Gemini + Google Search — same pipeline as Recommended jobs "Search Google for jobs" */
export interface GoogleAiJobsResult {
  message: string;
  googleJobsAdded: number;
  resumeId: string;
  jobUids?: string[];
}

export const fetchGoogleAiJobs = async (
  token: string,
  resumeId?: string | null,
  useGrounding: boolean = true,
): Promise<GoogleAiJobsResult> => {
  const url = buildURL(`/api/connections/google-ai-jobs`);
  if (resumeId?.trim()) {
    url.searchParams.set('resumeId', resumeId.trim());
  }
  url.searchParams.set('useGrounding', String(useGrounding));
  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    let msg = 'Google AI jobs fetch failed';
    try {
      const err = await response.json();
      msg = err.detail || err.message || msg;
    } catch {
      // keep default
    }
    throw new Error(msg);
  }
  return response.json();
};
